import { Badge } from '@/shared/components/Badge'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Users,
  AlertCircle,
  RefreshCw,
  Eye,
  Trash2,
  ClockFading,
  Search,
} from "lucide-react";
import { apiService, type UserProfile, type ApiError } from '@/services/api';
import { ConfirmarEliminacion } from '@/shared/components/ConfirmarEliminacion';
import { EliminacionBloqueada } from '@/shared/components/EliminacionBloqueada';
import { userHasPermission } from '@/shared/lib/roleUtils';
import { useAuth } from '@/shared/hooks/useAuth';
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage';
import FilterSelect from '@/shared/components/FilterSelect';
import { useFilaSaliente } from '@/shared/hooks/useFilaSaliente';
import { EsqueletoTabla } from '@/shared/components/Esqueleto';

type PrimaryRole = {
  key: string;
  name: string;
};

type PrimaryRoleMap = Record<string, PrimaryRole | null>;

const SIN_AREA_LABEL = "Sin Área/Departamento asignada";

// Prefijos de permisos por módulo (ver src/constants/permissions.ts) — mismo
// criterio que ya usa Sidebar.tsx para decidir a qué módulos tiene acceso un usuario.
const MODULE_PREFIXES = [
  { label: "Almacén", prefix: "almacen." },
  { label: "RH", prefix: "rh." },
  { label: "Proveedores", prefix: "proveedores." },
];

// ─── Filter dropdown ──────────────────────────────────────────────────────────

const UsersList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [porEliminar, setPorEliminar] = useState<
    { id: string; correo: string } | null
  >(null);
  /** Cuando el servidor rechaza la baja: qué la retiene y de quién. */
  const [bloqueado, setBloqueado] = useState<
    { nombre: string; motivo: string } | null
  >(null);
  const { claseDeFila, animarSalida, cancelarSalida } = useFilaSaliente();
  const [primaryRoles, setPrimaryRoles] = useState<PrimaryRoleMap>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [filterUsuario, setFilterUsuario] = useState("");
  const [filterRol, setFilterRol] = useState("");
  const [filterDepartamento, setFilterDepartamento] = useState("");
  const [filterSistema, setFilterSistema] = useState("");
  const [filterEstado, setFilterEstado] = useState("");

  // Load users on component mount
  useEffect(() => {
    loadUsers();
  }, []);

  const fetchPrimaryRoles = useCallback(async (usersList: UserProfile[]) => {
    if (!usersList.length) {
      setPrimaryRoles({});
      return;
    }

    const roleEntries = await Promise.all(
      usersList.map(async (user) => {
        try {
          const rbacData = await apiService.getUserRBAC(user.id);
          const primaryRole = rbacData.roles?.[0];

          return {
            userId: user.id,
            role: primaryRole
              ? {
                  key: primaryRole.key,
                  name: primaryRole.name || primaryRole.key,
                }
              : null,
          };
        } catch (rbacError) {
          console.error("Error loading primary role for user:", {
            userId: user.id,
            error: rbacError,
          });
          return {
            userId: user.id,
            role: null,
          };
        }
      }),
    );

    const roleMap: PrimaryRoleMap = {};
    roleEntries.forEach(({ userId, role }) => {
      roleMap[userId] = role;
    });

    setPrimaryRoles(roleMap);
  }, []);

  const loadUsers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const usersData = await apiService.getAllUsers();
      setUsers(usersData);
      setPrimaryRoles({});
      await fetchPrimaryRoles(usersData);
    } catch (err) {
      const apiError = err as ApiError;
      const message = Array.isArray(apiError.message)
        ? apiError.message.join(", ")
        : apiError.message || "Error al cargar usuarios";
      setError(message);
      console.error("Error loading users:", apiError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (userId: string, userEmail: string) => {
    setPorEliminar({ id: userId, correo: userEmail });
  };

  /**
   * El borrado en si, ya confirmado en el modal.
   *
   * Antes esto arrancaba con un window.confirm: se veia con el dominio arriba,
   * decia «says», y congelaba la pestana entera mientras estuviera abierto.
   */
  const eliminaConfirmado = async () => {
    if (!porEliminar) return;
    const { id: userId } = porEliminar;

    setPorEliminar(null);
    setDeleteLoading(userId);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await apiService.deleteUser(userId);

      // El backend desactiva en vez de borrar cuando el usuario tiene
      // historial. Antes la fila se quitaba igual: desaparecia de la pantalla,
      // el mensaje decia eliminado, y al recargar el usuario seguia ahi.
      const seElimino = response.eliminado !== false;

      if (seElimino) {
        // A-16: primero sale la fila, luego se quita del estado.
        await animarSalida(userId);
        setUsers((prevUsers) => prevUsers.filter((user) => user.id !== userId));
        setPrimaryRoles((prevRoles) => {
          const nextRoles = { ...prevRoles };
          delete nextRoles[userId];
          return nextRoles;
        });
      } else {
        // Se queda, pero como lo dejo el backend: inactivo y sin accesos.
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === userId
              ? { ...user, isActive: false, roles: [], area: undefined, areaId: undefined }
              : user
          )
        );
        setPrimaryRoles((prevRoles) => ({ ...prevRoles, [userId]: null }));
      }

      setSuccessMessage(response.message || "Usuario eliminado exitosamente");
      // Una desactivacion necesita explicarse, asi que su aviso dura mas.
      setTimeout(() => setSuccessMessage(null), seElimino ? 3000 : 8000);
    } catch (err) {
      cancelarSalida(userId);
      const apiError = err as ApiError;
      const message = Array.isArray(apiError.message)
        ? apiError.message.join(", ")
        : apiError.message || "Error desconocido";

      // 409 es el servidor diciendo que algo lo retiene y que hay que
      // resolverlo primero —herramientas en la mano, requisiciones a su
      // nombre—. Eso pide un modal: la franja de error de arriba se pasa por
      // alto y la persona lo vuelve a intentar sin enterarse de qué falta.
      if (apiError.statusCode === 409) {
        setBloqueado({ nombre: nombreDelUsuario(userId), motivo: message });
      } else {
        setError(`Error al eliminar usuario: ${message}`);
      }
      console.error("Error deleting user:", apiError);
    } finally {
      setDeleteLoading(null);
    }
  };

  const nombreDelUsuario = (userId: string) => {
    const u = users.find((x) => x.id === userId);
    if (!u) return '';
    return `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email;
  };

  const handleViewDetail = (userId: string) => {
    navigate(`/users/${userId}`);
  };

  const getFullName = (u: UserProfile) => `${u.firstName} ${u.lastName}`.trim();
  const getDepartamentoLabel = (u: UserProfile) => u.area?.nombre || SIN_AREA_LABEL;
  // Los "sistemas" de un usuario no vienen como campo aparte en el response:
  // se derivan de rbac.effectivePermissions — si tiene algún permiso que
  // empieza con "almacen.", "rh." o "proveedores.", quiere decir que ese
  // módulo lo tiene asignado. Master bypassa (mismo criterio que el Sidebar).
  const getSistemas = (u: UserProfile) => {
    const isMaster = u.rbac?.isMaster ?? false;
    const perms = u.rbac?.effectivePermissions || [];
    return MODULE_PREFIXES
      .filter(({ prefix }) => isMaster || perms.some((p) => p.startsWith(prefix)))
      .map((m) => m.label);
  };

  // ── Opciones únicas para cada select de filtro ──
  const uniqueUsuarios = useMemo(
    () =>
      Array.from(new Set(users.map((u) => getFullName(u)).filter(Boolean))).sort(
        (a, b) => a.localeCompare(b, "es"),
      ),
    [users],
  );
  const uniqueRoles = useMemo(() => {
    const names = users
      .map((u) => primaryRoles[u.id]?.name)
      .filter((n): n is string => !!n);
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, "es"));
  }, [users, primaryRoles]);
  const uniqueDepartamentos = useMemo(() => {
    const names = users.map((u) => u.area?.nombre).filter((n): n is string => !!n);
    return [
      ...Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, "es")),
      SIN_AREA_LABEL,
    ];
  }, [users]);
  // Los 3 módulos son un catálogo fijo — se muestran siempre como opción,
  // no solo cuando algún usuario cargado ya los tiene (igual que "Sin Área").
  const uniqueSistemas = MODULE_PREFIXES.map((m) => m.label);
  const ESTADO_OPTIONS = ["Activo", "Inactivo"];

  const handleLimpiarFiltros = () => {
    setSearchQuery("");
    setFilterUsuario("");
    setFilterRol("");
    setFilterDepartamento("");
    setFilterSistema("");
    setFilterEstado("");
  };

  // Filter users based on search query + selects
  const filteredUsers = users.filter((user) => {
    const fullName = getFullName(user);
    const email = user.email.toLowerCase();
    const primaryRole = primaryRoles[user.id];
    const roleLabel = primaryRole?.name || "";
    const departamentoLabel = getDepartamentoLabel(user);
    const sistemas = getSistemas(user);
    const estadoLabel = user.isActive ? "Activo" : "Inactivo";

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        fullName.toLowerCase().includes(query) ||
        email.includes(query) ||
        roleLabel.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }
    if (filterUsuario && fullName !== filterUsuario) return false;
    if (filterRol && roleLabel !== filterRol) return false;
    if (filterDepartamento && departamentoLabel !== filterDepartamento) return false;
    if (filterSistema && !sistemas.includes(filterSistema)) return false;
    if (filterEstado && estadoLabel !== filterEstado) return false;

    return true;
  });

  const hasActiveFilters =
    !!searchQuery || !!filterUsuario || !!filterRol || !!filterDepartamento || !!filterSistema || !!filterEstado;

  const canDeleteUsers = userHasPermission(
    "generales.usuarios.base.delete",
    user?.rbac?.effectivePermissions || [],
  );

  // A-01: la silueta de la tabla en lugar del spinner, para que la pantalla
  // conserve su forma mientras llegan los usuarios.
  if (isLoading) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
          <EsqueletoTabla filas={8} columnas={5} />
        </div>
      </div>
    );
  }

  if (
    userHasPermission(
      "generales.usuarios.base.read",
      user?.rbac?.effectivePermissions || [],
    ) === false
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <PermissionsDenyPage />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between mt-8">
        <div>
          <TituloDePantalla nivel="interna">
            Gestión de usuarios
          </TituloDePantalla>
          <p className="text-sm text-gray-600 mt-1">
            Administra aquí la lista de usuarios
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadUsers}
            disabled={isLoading}
            className={estiloDeBoton('secundario')}
          >
            <RefreshCw
              className={`w-4 h-4 ${isLoading ? "girando" : ""}`}
            />
            <span>Actualizar</span>
          </button>
          <button
            onClick={() => navigate('/users/audit-history')}
            className={estiloDeBoton('secundario')}
          >
            <ClockFading className="w-4 h-4" />
            <span>Historial</span>
          </button>
          {userHasPermission(
            "generales.usuarios.base.create",
            user?.rbac?.effectivePermissions || [],
          ) && (
            <button
              onClick={() => navigate('/users/new')}
              className={estiloDeBoton('primario')}
            >
              <Plus className="w-4 h-4" />
              <span>Nuevo usuario</span>
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-2">
          <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
            <svg
              className="w-3 h-3 text-white"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <p className="text-sm text-green-700">{successMessage}</p>
        </div>
      )}

      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar en usuarios..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          />
        </div>
        <FilterSelect label="Usuario"          options={(uniqueUsuarios).map((o) => ({ value: o, label: o }))}      value={filterUsuario}      onChange={setFilterUsuario} ancho="contenido"
          />
        <FilterSelect label="Rol"              options={(uniqueRoles).map((o) => ({ value: o, label: o }))}         value={filterRol}          onChange={setFilterRol} ancho="contenido"
          />
        <FilterSelect label="Departamento/área" options={(uniqueDepartamentos).map((o) => ({ value: o, label: o }))} value={filterDepartamento} onChange={setFilterDepartamento} ancho="contenido"
          />
        <FilterSelect label="Sistema"          options={(uniqueSistemas).map((o) => ({ value: o, label: o }))}      value={filterSistema}      onChange={setFilterSistema} ancho="contenido"
          />
        <FilterSelect label="Estado"           options={(ESTADO_OPTIONS).map((o) => ({ value: o, label: o }))}      value={filterEstado}       onChange={setFilterEstado} ancho="contenido"
          />
        {hasActiveFilters && (
          <button
            onClick={handleLimpiarFiltros}
            className="text-sm font-semibold text-[#315FB9] hover:underline"
          >
            Limpiar
          </button>
        )}
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#E5E7EB]">
            <thead className="bg-[#FAF9FF]">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Usuario
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Email
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Rol
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Departamento/Área
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Sistema
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                  Creado
                </th>
                <th className="px-6 py-4 text-center text-sm font-medium text-[#1B2128] tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E1E1E1]">
              {filteredUsers.map((user) => {
                const primaryRole = primaryRoles[user.id];
                const roleKey = (primaryRole?.key || "").toUpperCase();
                const roleLabel =
                  primaryRole?.name || (roleKey ?? "Sin rol asignado");

                return (
                  <tr
                    key={user.id}
                    className={`hover:bg-[#F4F7FB] transition-colors ${claseDeFila(user.id)}`}
                  >
                    <td className="px-6 py-4">
                      <div
                        onClick={() => handleViewDetail(user.id)}
                        className="cursor-pointer"
                      >
                        <div className="text-sm font-semibold text-gray-900 w-max">
                          {user.firstName} {user.lastName}
                        </div>
                      </div>
                    </td>
                    <td
                      className="px-6 py-4 text-sm text-gray-700 cursor-pointer"
                      onClick={() => handleViewDetail(user.id)}
                    >
                      {user.email}
                    </td>
                    <td
                      className="px-6 py-4 cursor-pointer"
                      onClick={() => handleViewDetail(user.id)}
                    >
                      <Badge color="morado">{roleLabel}</Badge>
                    </td>
                    <td
                      className="px-6 py-4 cursor-pointer"
                      onClick={() => handleViewDetail(user.id)}
                    >
                      <div className="text-sm text-gray-900 min-w-[200px]">
                        {getDepartamentoLabel(user)}
                      </div>
                    </td>
                    <td
                      className="px-6 py-4 cursor-pointer"
                      onClick={() => handleViewDetail(user.id)}
                    >
                      <div className="text-sm text-gray-900 min-w-[120px]">
                        {getSistemas(user).join(", ") || "—"}
                      </div>
                    </td>
                    <td
                      className="px-6 py-4 cursor-pointer"
                      onClick={() => handleViewDetail(user.id)}
                    >
                      <Badge color={user.isActive ? "verde" : "gris"}>
                        {user.isActive ? "Activo" : "Inactivo"}
                      </Badge>
                    </td>
                    <td
                      className="px-6 py-4 text-sm text-gray-600 cursor-pointer"
                      onClick={() => handleViewDetail(user.id)}
                    >
                      {new Date(user.createdAt).toLocaleDateString("es-ES", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleViewDetail(user.id)}
                          aria-label="Ver"
                          title="Ver detalles"
                          className="text-[#315FB9] hover:text-[#315FB9]/80 hover:underline inline-flex items-center space-x-1 text-sm font-semibold"
                        >
                          <Eye className="w-4 h-4" />
                          <span>Ver</span>
                        </button>
                        {canDeleteUsers && (
                          <button
                            onClick={() => handleDelete(user.id, user.email)}
                            disabled={deleteLoading === user.id}
                            aria-label="Eliminar"
                            title="Eliminar usuario"
                            className="inline-flex items-center justify-center w-8 h-8 text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                          >
                            {deleteLoading === user.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-600 border-t-transparent"></div>
                            ) : (
                              <Trash2 className="w-5 h-5" />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredUsers.length === 0 && !isLoading && (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">
                {hasActiveFilters
                  ? "No se encontraron usuarios con ese criterio de búsqueda"
                  : "No hay usuarios registrados"}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Users Count */}
      <div className="text-sm text-gray-600">
        {hasActiveFilters ? (
          <>
            Mostrando {filteredUsers.length} de {users.length} usuarios
          </>
        ) : (
          <>Total de usuarios: {users.length}</>
        )}
      </div>

      {bloqueado && (
        <EliminacionBloqueada
          nombre={bloqueado.nombre}
          motivo={bloqueado.motivo}
          onCerrar={() => setBloqueado(null)}
        />
      )}

      {porEliminar && (
        <ConfirmarEliminacion
          titulo="¿Eliminar usuario?"
          nombre={porEliminar.correo}
          detalle="Se borran también sus notificaciones, sus permisos y su expediente de empleado si lo tiene. Su actividad se conserva en la bitácora."
          trabajando={deleteLoading === porEliminar.id}
          onCancelar={() => setPorEliminar(null)}
          onConfirmar={eliminaConfirmado}
        />
      )}
    </div>
  );
};

export default UsersList;
